import React from "react";
import { Button, Link } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatchWrapper } from "../../../../../../hooks";
import { IStoreState } from "../../../../../../redux/initialStoreState";
import { useSelector } from "react-redux";
import { LoadState } from "../../../../../../constants/enums";
import { useTableV2State } from "../../../../../../components/TableV2/hooks/useTableV2State";
import { fetchHabitationalPolicyChangeListAsync } from "../../../../../../redux/habitationalPolicy/habitationalPolicyChangeRequest/habitationalPolicyChangeReqActions";
import { ControlledCustomSelect } from "../../../../../../components/formsComponents";
import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../../../../../components/TableV2/interfaces/IDataTableV2Props";
import { IHabitationalPolicyChange } from "../../../../../../redux/habitationalPolicy/habitationalPolicyChangeRequest/habitationalPolicyChangeReq.types";
import { StatusRenderer } from "../../../../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { DataTableV2 } from "../../../../../../components/TableV2/DataTableV2";
import {
  TASK_STATUS,
} from "../../../../../../constants/constants";
import { PageTitleBar } from "../../../../../../components/PageTitleBar/PageTitleBar";
import { PageContainer } from "../../../../../../components/container/PageContainer";
import { StandardTableActions } from "../../../../../../components/StandardTableActions/StandardTableActions";
import { AddOutlined, Assignment } from "@mui/icons-material";
import { AssignedToDialog } from "../../../../../tasks/PolicyChangeRequest/AssignedToDialog";
import { useAuth } from "../../../../../../contexts/AuthProvider/AuthProvider";
import { DataTableV2FilterButton } from "../../../../../../components/TableV2/components/DataTableV2FilterButton/DataTableV2FilterButton";
import {
  downloadAutomobilePolicyChangeReqPDF,
  previewAutomobilePolicyChangeReqPDF,
} from "../../../../../../redux/automobilePolicyChangeReq/automobilePolicyChangeReqActions";

export const HabitationalPolicyChangeList: React.FC = (props) => {
  const { customerId, customerPolicyId } = useParams() as {
    customerId?: number;
    customerPolicyId?: number;
  };
  const navigate = useNavigate();
  const { list, totalRecords, loading } = useSelector(
    (storeState: IStoreState) => storeState.habitational.policyChangeReq
  );
  const [assigned, setAssigned] =
    React.useState<IHabitationalPolicyChange | null>(null);
  const dispatch = useDispatchWrapper();
  const {
    state: { dateState, searchState, tabs, columnsConfig,
      dense },
    setDateState,
    setSelectedTab,
    setTableTabs,
    setSearchState,
    setColumnVisibility,
 
    onDenseToggle, } = useTableV2State({
    filtersInitialState: {
      defaultDateRange: "last7Days",
      selectedTab: "-1",
    },
  });
  const {
    user: { userId },
  } = useAuth();

  const [status, setStatus] = React.useState<string>("-1");

  const handleCreate = () => {
    navigate(
      `/habitational-policy/${customerId}/policy-change-request/${customerPolicyId}`
    );
  };

  const fetchList = () => {
    if (customerPolicyId) {
      dispatch(
        fetchHabitationalPolicyChangeListAsync(
          customerPolicyId,
          status,
          searchState,
          dateState.dates
        )
      );
    }
  };

  const policyChangeTableProps: IDataTableV2Props = {
    isPagination: false,
    isDataLoading: loading === LoadState.InProgress,
    totalRecords: totalRecords,
    tableCommandBarProps: {
      preDefinedPlugins: {
        dateFilter: {
          state: dateState,
          onChange: setDateState,
        },
        columnVisibility: {
          columnVisibility: columnsConfig.columnVisibility,
          onChange: setColumnVisibility,
        },
        search: {
          state: searchState,
          excludeColumnsInSearch: ["insert_ts"],
          showMyRecordsButton: {
            columnName: "assigned_to_id",
            currentUserId: userId,
          },
          onChange: setSearchState,
          loadInitialFilterOncePopoverOpened: [
            {
              column: ["customer_name"],
              logicalOperator: "OR",
              operator: "CONTAINS",
              value: "",
            },
          ],
        },

        refresh: {
          onClick: fetchList,
        },
      },
      leftItems: {
        customPlugins: [
          {
            key: "status",
            onRender: () => (
              <ControlledCustomSelect
                sx={{ minWidth: "120px" }}
                value={status}
                onChange={(e) => setStatus(e.target.value as "ACTIVE")}
                options={[{ label: "All", value: "-1" }, ...TASK_STATUS]}
              ></ControlledCustomSelect>
            ),
          },
        ],
      },
      rightItems: {
        customPlugins: [
          {
            key: "assignedToButton",
            onRender: () => {
              return (
                <DataTableV2FilterButton
                  icon={Assignment}
                  id="assign-to-me"
                  label="Assign to me"
                  onClick={() => {
                    setSearchState([
                      ...searchState,
                      {
                        column: ["assigned_to_id"],
                        operator: "EQUAL",
                        logicalOperator: "OR",
                        value: userId.toString(),
                      },
                    ]);
                  }}
                />
              );
            },
          },
        ],
      },
    },
    masterColumns: [
      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        width: "220px",
        isFirstColumnSticky: true,
        disableResize: true,
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IHabitationalPolicyChange) => {
          return (
            <StandardTableActions
              historyCompData={{ module_code: row.change_request_code as any }}
              onEditClick={() => {
                navigate(
                  `/habitational-policy/${customerId}/policy-change-request/${customerPolicyId}/manage/${row.change_request_code}`
                );
              }}
              onViewClick={() => {
                navigate(
                  `/habitational-policy/${customerId}/policy-change-request/${customerPolicyId}/manage/${row.change_request_code}`
                );
              }}
              onAssignedTo={() => {
                setAssigned(row);
              }}
              onDownLoadClick={() => {
                dispatch(
                  downloadAutomobilePolicyChangeReqPDF(
                    customerPolicyId ?? 0,
                    row.change_request_code ?? "",
                    "habitational"
                  )
                );
              }}
              onDownloadPreview={() => {
                dispatch(
                  previewAutomobilePolicyChangeReqPDF(
                    customerPolicyId ?? 0,
                    row.change_request_code ?? "",
                    "habitational"
                  )
                );
              }}
            />
          );
        },
      },
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        // exportCellWidth: 25,
        onRowCellRender: (values, row: IHabitationalPolicyChange) => {
          return (
            <StatusRenderer
              status={row.status === "Active" ? "New" : row.status}
            />
          );
        },
      },
      {
        key: "change_request_code",
        headerName: "Code",
        fieldName: "change_request_code",
        renderType: DataTableV2RowRenderType.TEXT,
        // exportCellWidth: 15,
        enableSorting: true,
        onRowCellRender: (value, row: IHabitationalPolicyChange) => {
          return (
            <Link
              sx={{ cursor: "pointer" }}
              onClick={() => {
                navigate(
                  `/habitational-policy/${customerId}/policy-change-request/${customerPolicyId}/manage/${row.change_request_code}`
                );
              }}
            >
              {row.change_request_code}
            </Link>
          );
        },
      },

      {
        key: "customer_name",
        headerName: "Customer Name",
        fieldName: "customer_name",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "customer_code",
        headerName: "Customer Code",
        fieldName: "customer_code",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.TEXT_DARK,
        // onRowCellRender: (value, row: ITask) => {
        //   return <LeadOppStatus status={row.task_status} />;
        // },
      },
      {
        key: "policy_number",
        headerName: "Policy Number",
        fieldName: "policy_number",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.TEXT_DARK,
      },
      {
        key: "assigned_to_name",
        headerName: "Assigned To",
        fieldName: "assigned_to_name",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.TEXT_DARK,
      },

      {
        key: "sub_type",
        headerName: "Type",
        fieldName: "sub_type",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.TEXT_DARK,
      },
      {
        key: "effective_date",
        headerName: "Transaction Effective Date",
        fieldName: "effective_date",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.DATE_TIME,
      },
      {
        key: "days_left",
        headerName: "Days Left",
        fieldName: "days_left",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.TEXT_DARK,
      },
      {
        key: "insurer",
        headerName: "Insurer",
        fieldName: "insurer",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.TEXT_DARK,
      },
      {
        key: "policy_effective_date",
        headerName: "Effective Date",
        fieldName: "policy_effective_date",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.DATE_TIME,
      },
      {
        key: "expiry_date",
        headerName: "Expiry Date",
        fieldName: "expiry_date",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.DATE_TIME,
      },

      {
        key: "policy_type",
        headerName: "Line of Business",
        fieldName: "policy_type",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.TEXT_DARK,
      },
      {
        key: "branch_code",
        headerName: "Branch Code",
        fieldName: "branch_code",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.CHIP_SUCCESS,
      },
      {
        key: "producer_one_code",
        headerName: "Producer Code",
        fieldName: "producer_one_code",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.CHIP_WARNING,
      },
      {
        key: "due_date",
        headerName: "Due Date",
        fieldName: "due_date",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.DATE_TIME,
      },
      {
        key: "date_completed",
        headerName: "Date Completed",
        fieldName: "date_completed",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.DATE_TIME,
      },
      // {
      //   key: "type",
      //   headerName: "Type",
      //   fieldName: "type",
      //   renderType: DataTableV2RowRenderType.TEXT,
      //   // exportCellWidth: 30,
      //   enableSorting: true,
      //   onRowCellRender: (values, row: IPolicyChangeRequest) => {
      //     const formattedLabels: Record<string, string> = {
      //       GeneralChange: "General Change",
      //       PolicyChange: "Policy Change",
      //       LOA: "LOA",
      //       LOE: "LOE",
      //       "New(upload)": "New (upload)",
      //       "Remarket(upload)": "Remarket (upload)",
      //       CertificateOfInsurance: "Certificate of Insurance",
      //       QuoteRequest: "Quote Request",
      //       PaymentAuthorization: "Payment Authorization",
      //       CancellationReinstatement: "Cancellation / Reinstatement",
      //       New: "New",
      //       PaymentMethod: "Payment Method",
      //       LiabilityInsurance: "Liability Insurance",
      //     };

      //     return formattedLabels[row.type ?? ""] || row.type;
      //   },
      // },

      {
        key: "created_by_name",
        headerName: "Created By",
        fieldName: "created_by_name",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.TEXT_DARK,
      },
      {
        key: "date_created",
        headerName: "Date Created",
        fieldName: "date_created",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.DATE_TIME,
      },
      {
        key: "modified_by_name",
        headerName: "Modified By",
        fieldName: "modified_by_name",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.TEXT_DARK,
      },

      {
        key: "insert_ts",
        headerName: "Date Modified",
        fieldName: "insert_ts",
        renderType: DataTableV2RowRenderType.DATE_TIME_DARK_COLOR,
      },
    ],
    // tableTabProps: {
    //   selectedTab: tabs.selectedTab,
    //   tabs: tabs.tabs,
    //   onTabChange: (newSelectedTab) => {
    //     setSelectedTab(newSelectedTab);
    //   },
    // },
    rows: list,
      dense: dense,
    onDenseToggle: onDenseToggle,};

  const handleComplete = () => {
    fetchList();
    setAssigned(null);
  };

  React.useEffect(() => {
    fetchList();
  }, [status, searchState, dateState.dates]);

  return (
    <PageContainer
      title="Policy Changes/Upload"
      description="this is Policy Change Request list"
    >
      <PageTitleBar
        rightHeading={
          <Button
            variant="contained"
            onClick={handleCreate}
            startIcon={<AddOutlined />}
          >
            Create
          </Button>
        }
      />
      <DataTableV2 {...policyChangeTableProps} />
      {assigned && (
        <AssignedToDialog
          open={true}
          customerPolicyId={assigned.customer_policy_id}
          customerId={assigned.customer_id}
          change_request_code={assigned.change_request_code as any}
          assignedTo={assigned.assigned_to_id}
          status={assigned.status}
          policyType={assigned.policy_type}
          onClose={() => setAssigned(null)}
          onComplete={handleComplete}
        />
      )}
    </PageContainer>
  );
};
